<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { mapValues } from 'lodash-es';
import { assetSchema } from '@/validation';
import type { AssetDataEntry, PropertyOwnership } from '@/types';
import { TrashIcon } from '@heroicons/vue/24/outline';
import { AssetType, propertyPurposeOptions, YesNoOptions } from '@/helpers/const';
import PropertyAssetOwnershipForm from '@/components/shared/PropertyAssetOwnershipForm.vue';
import IncomingPropertyIsNewBuild from '@/components/application/incoming-property-is-new-build.vue';
import IncomingPropertyContractExchangedBeforeMay2026 from '@/components/application/incoming-property-contract-exchanged-before-may-2026.vue';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import { useAutoPopulateMotorVehicle } from '@/helpers/useAutoPopulateMotorVehicle';
import { useNegativeGearingFy27Asset } from '@/helpers/useNegativeGearingFy27Asset';

const props = withDefaults(
  defineProps<{
    applicantId: string;
    assetNumber: number;
    modelValue: AssetDataEntry;
    hasEndDebt?: boolean;
    ownershipParticipants?: Record<string, string>;
  }>(),
  {
    hasEndDebt: false,
    ownershipParticipants: undefined,
  },
);

const { loanApp, loanApplicants } = useBrokerLoanAppProcess();

const applicantIndex = computed<number>(() => {
  return loanApp.value.applicants.findIndex((item) => item.id === props.applicantId);
});

const emit = defineEmits<{
  (e: 'update:modelValue', v: AssetDataEntry): void;
  (e: 'save'): void;
  (e: 'remove', assetNumber: number): void;
}>();

const assetOptions = {
  [AssetType.GIFTED_FUND]: { label: 'Gifted fund', description: 'Gift' },
  [AssetType.PROPERTY]: { label: 'Property', description: 'Property' },
  [AssetType.SAVINGS]: { label: 'Savings account', description: 'Savings' },
  [AssetType.SHARES]: { label: 'Share portfolio', description: 'Shares' },
  [AssetType.SUPERANNUATION]: { label: 'Superannuation fund', description: 'Superannuation' },
  [AssetType.MOTOR_VEHICLES]: { label: 'Motor vehicle', description: 'Motor vehicle' },
  [AssetType.MACHINERY]: { label: 'Machinery', description: 'Machinery' },
  [AssetType.EQUIPMENT]: { label: 'Equipment', description: 'Equipment' },
};

const purposeOptions = {
  investment_property: {
    label: propertyPurposeOptions.investment_property,
    description: 'Investment property',
  },
  owner_occupied: {
    label: propertyPurposeOptions.owner_occupied,
    description: 'Owner occupied',
  },
};

const newItem = ref(!props.modelValue.type);
const showEntry = ref(!props.modelValue.type);

const { values, meta, submitCount, handleSubmit, resetForm, setFieldValue } = useForm<AssetDataEntry>({
  validationSchema: assetSchema,
  validateOnMount: false,
});

const initPropertyOwnership = (): PropertyOwnership => {
  const participantIds = props.ownershipParticipants
    ? Object.keys(props.ownershipParticipants)
    : loanApp.value.applicants.map((a) => a.id);

  const defaultShares = participantIds.reduce((obj, id) => {
    obj[id] = 0;
    return obj;
  }, {} as Record<string, number>);

  const existingShares = props.modelValue.propertyOwnership?.share;
  const participantsChanged = existingShares && Object.keys(existingShares).length !== participantIds.length;

  return props.modelValue.propertyOwnership && !participantsChanged
    ? { ...props.modelValue.propertyOwnership }
    : { id: `asset-${props.assetNumber}`, type: 'asset', share: defaultShares };
};

const propertyOwnership = ref<PropertyOwnership>(initPropertyOwnership());

const ownershipLoanApplicants = computed(() => {
  if (!props.ownershipParticipants) return loanApplicants.value;
  return mapValues(props.ownershipParticipants, (name) => ({ firstName: name }));
});

const isMotorVehicle = computed(() => values.type === AssetType.MOTOR_VEHICLES);
const isDescriptionAsset = computed(() => values.type === AssetType.MACHINERY || values.type === AssetType.EQUIPMENT);
const isNonPropertyAsset = computed(() => !!values.type && values.type !== AssetType.PROPERTY);

const { showNewBuild, showContractExchange } = useNegativeGearingFy27Asset(
  computed(() => values.propertyPurpose),
  computed(() => values.isNewBuild),
);

const descriptionLabel = computed(() => {
  if (values.type === AssetType.MACHINERY) return 'Machinery details';
  if (values.type === AssetType.EQUIPMENT) return 'Equipment details';
  return 'Details';
});

const valueLabel = computed(() => {
  if (isMotorVehicle.value) return 'Asset value';
  if (isDescriptionAsset.value) return 'Estimated value';
  return 'Value';
});

const getAssetDescription = (): string => {
  if (!values.type || newItem.value) return '';
  if (values.type === AssetType.PROPERTY) return values.address?.addressLine ?? '';
  if (values.type === AssetType.MOTOR_VEHICLES) return [values.make, values.model].filter(Boolean).join(' ');
  if (values.type === AssetType.MACHINERY || values.type === AssetType.EQUIPMENT) return values.description ?? '';
  return values.institution ?? '';
};

const cardTitle = computed(() => {
  if (!values.type) return 'Select asset';
  const option = assetOptions[values.type as keyof typeof assetOptions];
  if (newItem.value) return option.label;
  const description = getAssetDescription();
  return description ? `${option.description}: ${description}` : option.label;
});

const showResidualDebtQuestions = computed(() => {
  if (!props.hasEndDebt || !values.type) return false;
  return values.type !== AssetType.PROPERTY && values.type !== AssetType.GIFTED_FUND;
});

const update = handleSubmit(
  (values) => {
    showEntry.value = false;
    newItem.value = false;
    emit('update:modelValue', { ...values, propertyOwnership: propertyOwnership.value });
    return true;
  },
  () => {
    showEntry.value = true;
  },
);

const cancel = () => {
  resetForm();
  showEntry.value = false;
};

const editItem = () => {
  showEntry.value = true;
};

watch(
  () => props.modelValue,
  (data) => {
    const values = { ...data };
    resetForm({ values });
  },
  { immediate: true, deep: true },
);

// Auto-populate make and model from existing motor vehicle scenarios
useAutoPopulateMotorVehicle(
  loanApp,
  props.applicantId,
  computed(() => values.type),
  computed(() => values.make),
  computed(() => values.model),
  setFieldValue,
  true,
);

const actionSection = ref<HTMLElement>();
let timeout: ReturnType<typeof setTimeout>;
const focus = () => {
  clearTimeout(timeout);
  timeout = setTimeout(() => {
    actionSection.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, 300);
};

defineExpose({ update, cancel, focus });

const showModal = ref(false);
const openModal = () => {
  showModal.value = true;
};
const removeItem = () => {
  showModal.value = false;
  emit('remove', props.modelValue.assetNumber || 0);
};
</script>

<template>
  <div ref="actionSection">
    <BxExpander v-model="showEntry" separator>
      <template #header>
        <div class="py-5 pl-3 sm:pl-4">
          <div class="flex flex-wrap items-center gap-3 sm:flex-nowrap">
            <BxIcon class="text-bridgit-green" name="income" />
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              {{ cardTitle }}
            </h3>
            <div class="flex-grow"></div>
            <div v-if="!showEntry" class="flex-shrink-0">
              <BxButton v-if="!newItem" variant="secondary" @click="editItem()">Edit</BxButton>
              <BxButton variant="link" class="ml-4" @click="openModal()">Remove</BxButton>
            </div>
          </div>
        </div>
      </template>
      <template #body>
        <div class="p-6">
          <BxField v-slot="{ field }" name="type">
            <BxRadio
              v-bind="field"
              :id="applicantId + '-asset-' + assetNumber + '-type'"
              :options="assetOptions"
              label="Do you have any of the following assets?"
              label-type="default"
              block
              class="form-control mb-12"
            />
          </BxField>
          <div v-if="!values.type"></div>
          <template v-else-if="values.type === AssetType.PROPERTY">
            <BxField v-slot="{ field }" name="address">
              <BxAddressInput
                :id="applicantId + '-asset-' + assetNumber + '-property-address'"
                v-bind="field"
                label="Property Address"
                label-type="floating"
                class="form-control"
              />
            </BxField>
            <BxField v-slot="{ field }" name="propertyPurpose">
              <BxRadio
                v-bind="field"
                :id="'applicant-' + applicantIndex + '-asset-' + assetNumber + '-property-purpose'"
                :options="purposeOptions"
                label="What is the purpose of this property?"
                label-type="default"
                block
                class="form-control mb-12"
              />
            </BxField>
            <IncomingPropertyIsNewBuild v-if="showNewBuild" :id="applicantId + '-asset-' + assetNumber" />
            <IncomingPropertyContractExchangedBeforeMay2026
              v-if="showContractExchange"
              :id="applicantId + '-asset-' + assetNumber"
            />
            <BxField v-slot="{ field }" name="propertyValue">
              <BxNumberInput
                v-bind="field"
                :id="applicantId + '-asset-' + assetNumber + '-property-value'"
                label="Property value"
                label-type="floating"
                leading-symbol="$"
                class="form-control"
              />
            </BxField>
            <BxField v-slot="{ field }" name="rentalIncome">
              <BxPeriodicAmount
                :id="applicantId + '-asset-' + assetNumber + '-property-rental-income'"
                v-bind="field"
                label="Rental income"
                label-type="floating"
                class="form-control"
              />
            </BxField>
            <BxField v-slot="{ field }" name="amountOwing">
              <BxNumberInput
                v-bind="field"
                :id="applicantId + '-asset-' + assetNumber + '-property-amount-owed'"
                label="Amount owing"
                label-type="floating"
                leading-symbol="$"
                class="form-control"
              />
            </BxField>
            <div class="asset-property-ownership form-control mt-10">
              <h4 class="text-lg">
                <div class="font-medium">Property ownership</div>
              </h4>
              <PropertyAssetOwnershipForm
                v-model="propertyOwnership.share"
                :asset-applicant-id="applicantId"
                :asset-number="assetNumber"
                :loan-applicants="ownershipLoanApplicants"
                class="mt-2"
              />
            </div>
          </template>
          <template v-else-if="isMotorVehicle">
            <BxField v-slot="{ field }" name="make">
              <BxInput
                :id="applicantId + '-asset-' + assetNumber + '-make'"
                v-bind="field"
                label="Vehicle make"
                label-type="floating"
                class="form-control"
              />
            </BxField>
            <BxField v-slot="{ field }" name="model">
              <BxInput
                :id="applicantId + '-asset-' + assetNumber + '-model'"
                v-bind="field"
                label="Vehicle model"
                label-type="floating"
                class="form-control"
              />
            </BxField>
            <BxField v-slot="{ field }" name="yearBought">
              <BxInput
                :id="applicantId + '-asset-' + assetNumber + '-year-bought'"
                v-bind="field"
                label="Year bought"
                label-type="floating"
                class="form-control"
              />
            </BxField>
          </template>
          <template v-else-if="isDescriptionAsset">
            <BxField v-slot="{ field }" name="description">
              <BxInput
                :id="applicantId + '-asset-' + assetNumber + '-description'"
                v-bind="field"
                :label="descriptionLabel"
                label-type="floating"
                class="form-control"
              />
            </BxField>
          </template>
          <template v-else-if="isNonPropertyAsset">
            <BxField v-slot="{ field }" name="institution">
              <BxInput
                :id="applicantId + '-asset-' + assetNumber + '-institution'"
                v-bind="field"
                label="Name of institution"
                label-type="floating"
                class="form-control"
              />
            </BxField>
          </template>
          <BxField v-if="isNonPropertyAsset" v-slot="{ field }" name="value">
            <BxNumberInput
              v-bind="field"
              :id="applicantId + '-asset-' + assetNumber + '-amount'"
              :label="valueLabel"
              label-type="floating"
              leading-symbol="$"
              class="form-control"
            />
          </BxField>
          <template v-if="showResidualDebtQuestions">
            <BxField v-slot="{ field }" name="useAssetForResidualDebt">
              <BxRadio
                :id="applicantId + '-asset-' + assetNumber + '-use-for-residual-debt'"
                :options="YesNoOptions"
                v-bind="field"
                boolean
                class="form-control mb-12"
                label="Will the borrower be using this asset towards repaying the residual debt?"
                label-type="default"
              />
            </BxField>
            <BxField v-if="values.useAssetForResidualDebt === true" v-slot="{ field }" name="amountTowardsResidualDebt">
              <BxNumberInput
                v-bind="field"
                :id="applicantId + '-asset-' + assetNumber + '-amount-towards-residual-debt'"
                label="Amount towards residual debt"
                label-type="floating"
                leading-symbol="$"
                class="form-control"
              />
            </BxField>
          </template>

          <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
            Please review the error messages above.
          </BxErrorPanel>
          <div class="mt-10 flex-shrink-0">
            <BxButton :id="applicantId + '-asset-' + assetNumber + '-save'" variant="secondary" @click="update()"
              >Save</BxButton
            >
            <BxButton variant="link" class="ml-4" @click="newItem ? openModal() : cancel()">Cancel</BxButton>
          </div>
        </div>
      </template>
    </BxExpander>
    <BxModal
      :open="showModal"
      :icon="TrashIcon"
      title="Remove asset"
      confirm-label="Remove"
      cancel-label="Confirm"
      @close="showModal = false"
      @confirm="removeItem()"
    >
      Do you want to remove
      <span class="text-bridgit-green">{{ newItem ? 'the new asset' : cardTitle }}</span
      >?
    </BxModal>
  </div>
</template>
<style lang="scss" scoped>
.asset-property-ownership {
  padding: 1rem;
  background-color: #f5f9fc;
}
</style>
